import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { useStore } from "../context/StoreContext";
import { calendarMonth } from "../lib/finance";
import { fmt, fmt0, monthLabel, monthKey, today, dateLabel } from "../lib/format";
import { Section, Kpi, Money, Banner } from "./common";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";

const shift = (mk, n) => {
  const [y, m] = mk.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function DayCell({ d, selected, onClick, onDropBill, dragOver, setDragOver }) {
  const income = d.expected.filter((e) => e.isIncome);
  const bills = d.expected.filter((e) => !e.isIncome);
  const droppable = !d.isPast;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onDragOver={(e) => { if (droppable) { e.preventDefault(); setDragOver(d.date); } }}
      onDragLeave={() => setDragOver(null)}
      onDrop={(e) => { e.preventDefault(); setDragOver(null); if (droppable) onDropBill(e.dataTransfer.getData("text/plain"), d.date, e.shiftKey); }}
      data-testid={`calendar-day-${d.date}`}
      className={cn("min-h-[84px] sm:min-h-[112px] rounded-lg border p-1.5 sm:p-2 text-left flex flex-col gap-1 cursor-pointer transition-colors duration-150 hover:border-primary/60", d.isPast && "bg-secondary/40", d.isToday && "ring-2 ring-primary", selected && "border-primary bg-accent", d.low && !d.isPast && "border-[hsl(var(--expense)/0.6)]", dragOver === d.date && "border-primary border-dashed bg-accent")}
    >
      <span className={cn("text-xs font-semibold num", d.isToday && "text-primary")}>{Number(d.date.slice(8))}</span>
      {income.length > 0 && <span className="text-[10px] sm:text-[11px] rounded px-1 bg-income-soft text-income truncate" title={income.map((e) => e.name).join(", ")}>+{fmt0(income.reduce((s, e) => s + e.amount, 0))} pay</span>}
      {bills.slice(0, 3).map((b, i) => (
        <span
          key={`${b.itemKey}-${b.originalDate}`}
          draggable
          onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("text/plain", JSON.stringify([b.itemKey, b.originalDate])); e.dataTransfer.effectAllowed = "move"; }}
          onClick={(e) => e.stopPropagation() || onClick()}
          title={`${b.name} ${fmt(-b.amount)} — drag to another day${b.moved ? ` (moved from ${b.originalDate})` : ""}`}
          className={cn("text-[10px] sm:text-[11px] rounded px-1 bg-expense-soft text-expense truncate cursor-grab active:cursor-grabbing select-none", b.moved && "border border-dashed border-[hsl(var(--expense))]")}
          data-testid={`cal-bill-${b.originalDate}-${i}`}
        >
          {b.name} {fmt0(-b.amount)}
        </span>
      ))}
      {bills.length > 3 && <span className="text-[10px] text-expense">+{bills.length - 3} more</span>}
      {d.isPast && (d.actualOut > 0 || d.actualIn > 0) && (
        <span className="text-[10px] sm:text-[11px] text-muted-foreground num leading-tight">
          {d.actualIn > 0 && <span className="text-income block">+{fmt0(d.actualIn)}</span>}
          {d.actualOut > 0 && <span className="block">−{fmt0(d.actualOut)}</span>}
        </span>
      )}
      {!d.isPast && d.balance !== null && <span className={cn("mt-auto text-[10px] num hidden sm:block", d.low ? "text-expense font-semibold" : "text-muted-foreground")} data-testid={`calendar-balance-${d.date}`}>≈ {fmt0(d.balance)}</span>}
    </div>
  );
}

export default function SpendingCalendar() {
  const { state, moveBill, moveBillSeries } = useStore();
  const [series, setSeries] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [mk, setMk] = useState(monthKey(today()));
  const [sel, setSel] = useState(null);
  const cal = useMemo(() => calendarMonth(state, mk), [state, mk]);
  const day = cal.days.find((d) => d.date === sel);
  const lowDays = cal.days.filter((d) => d.low && !d.isPast);
  const isFuture = mk >= monthKey(today());
  const allEvents = cal.days.flatMap((d) => d.expected);
  const doMove = (ev, to, all = false) => {
    if (!ev || !to || to === ev.date) return;
    if (to < today()) return toast.error("Bills can only be moved to today or a future date");
    setSeries(false);
    if (all) {
      moveBillSeries(ev, to);
      return toast.success(`${ev.name} now usually due around day ${Number(to.slice(8))}`, { description: "Every future payment follows the new date. Use Undo at the top to revert." });
    }
    const lowest = (c) => Math.min(...c.days.filter((d) => !d.isPast && d.balance !== null).map((d) => d.balance));
    const nextMoves = [...(state.billMoves || []).filter((m) => !(m.itemKey === ev.itemKey && m.from === ev.originalDate)), ...(to !== ev.originalDate ? [{ itemKey: ev.itemKey, from: ev.originalDate, to }] : [])];
    const after = calendarMonth({ ...state, billMoves: nextMoves }, mk);
    const [a, b] = [lowest(cal), lowest(after)];
    moveBill(ev, to);
    toast.success(`Moved ${ev.name} to ${dateLabel(to)}`, { description: Number.isFinite(a) && Number.isFinite(b) ? `Lowest projected balance this month: ${fmt0(a)} → ${fmt0(b)}` : "Balances updated", action: { label: "Every payment", onClick: () => moveBillSeries(ev, to) }, cancel: { label: "Undo", onClick: () => moveBill(ev, ev.originalDate) } });
  };
  const onDropBill = (payload, to, all) => {
    let itemKey, originalDate;
    try { [itemKey, originalDate] = JSON.parse(payload); } catch { return; }
    doMove(allEvents.find((e) => e.itemKey === itemKey && e.originalDate === originalDate), to, all);
  };
  const moves = (state.billMoves || []).filter((m) => m.to.startsWith(mk) || m.from.startsWith(mk));

  return (
    <div className="space-y-5" data-testid="spending-calendar">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => { setMk(shift(mk, -1)); setSel(null); }} data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <h3 className="font-display text-xl font-bold min-w-[170px] text-center" data-testid="calendar-month-label">{monthLabel(mk)}</h3>
          <Button variant="outline" size="icon" onClick={() => { setMk(shift(mk, 1)); setSel(null); }} data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setMk(monthKey(today())); setSel(today()); }} data-testid="calendar-today-button">Today</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label={isFuture ? "Expected paydays" : "Money in"} value={fmt0(isFuture ? cal.totals.expectedIncome + cal.totals.actualIn : cal.totals.actualIn)} tone="income" testid="calendar-income" hint={isFuture && cal.totals.actualIn ? `${fmt0(cal.totals.actualIn)} already received` : undefined} />
        <Kpi label={isFuture ? "Bills still due" : "Money out"} value={fmt0(isFuture ? cal.totals.expectedBills : cal.totals.actualOut)} tone="expense" testid="calendar-bills" hint={isFuture && cal.totals.actualOut ? `${fmt0(cal.totals.actualOut)} spent so far` : undefined} />
        <Kpi label="Bills this month" value={cal.days.reduce((s, d) => s + d.expected.filter((e) => !e.isIncome).length, 0)} testid="calendar-bill-count" />
        <Kpi label="Tight days" value={lowDays.length} tone={lowDays.length ? "expense" : undefined} testid="calendar-low-days" hint={`Projected below ${fmt0(state.settings.lowBalance || 0)}`} />
      </div>
      {lowDays.length > 0 && <Banner tone="danger" icon={AlertTriangle} testid="calendar-low-banner">Your balance is projected to run low from <b>{dateLabel(lowDays[0].date)}</b>. Consider moving a bill after your next payday or holding back on everyday spending that week.</Banner>}
      <Section className="p-2 sm:p-4">
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {WEEK.map((w) => <div key={w} className="text-[11px] uppercase tracking-wider text-muted-foreground text-center py-1">{w}</div>)}
          {Array.from({ length: cal.firstWeekday }, (_, i) => <div key={`b${i}`} />)}
          {cal.days.map((d) => <DayCell key={d.date} d={d} selected={sel === d.date} onClick={() => setSel(d.date)} onDropBill={onDropBill} dragOver={dragOver} setDragOver={setDragOver} />)}
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-4 px-1">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-income-soft border border-[hsl(var(--income))]" /> Payday (expected)</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-expense-soft border border-[hsl(var(--expense))]" /> Bill due (expected)</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-secondary" /> Past days show actual activity</span>
          <span>≈ projected balance (estimate)</span>
          <span className="font-medium text-foreground">Tip: drag a bill to another day to see how your balance changes (hold Shift to move every future payment).</span>
        </div>
      </Section>
      {moves.length > 0 && (
        <Section title="Bills you've moved" subtitle="Only these occurrences are moved; future ones stay on their usual date. Forecasts use the new dates." testid="moved-bills">
          <ul className="divide-y text-sm">
            {moves.map((m) => (
              <li key={`${m.itemKey}-${m.from}`} className="py-2 flex justify-between items-center gap-2">
                <span>{m.name}: {dateLabel(m.from)} → <b>{dateLabel(m.to)}</b></span>
                <Button size="sm" variant="ghost" onClick={() => moveBill({ itemKey: m.itemKey, originalDate: m.from, name: m.name }, m.from)} data-testid={`undo-move-${m.itemKey}-${m.from}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {day && (
        <Section title={dateLabel(day.date)} subtitle={day.isPast ? "Actual transactions" : "Expected — from your confirmed bills and detected patterns"} testid="calendar-day-detail">
          {day.expected.some((e) => !e.isIncome) && (
            <label className="flex items-center gap-2 text-sm mb-2" data-testid="move-series-label">
              <Switch checked={series} onCheckedChange={setSeries} data-testid="move-series-switch" />
              {series ? "Changing the date moves every future payment (new usual date)" : "Changing the date moves only this payment"}
            </label>
          )}
          {day.expected.length > 0 && (
            <ul className="divide-y text-sm mb-3">
              {day.expected.map((e, i) => (
                <li key={i} className="py-2 flex flex-wrap justify-between gap-2" data-testid={`day-event-${i}`}>
                  <span>{e.name}{e.moved && <span className="block text-xs text-muted-foreground">Moved from {dateLabel(e.originalDate)}</span>}</span>
                  <span className="flex items-center gap-2 flex-wrap">
                    {e.confirmed ? <Badge variant="outline">Confirmed</Badge> : <Badge className="bg-warning-soft text-warning border-0">Predicted</Badge>}
                    <Money value={e.amount} signed />
                    {!e.isIncome && <Input type="date" min={today()} value={e.date} onChange={(x) => doMove(e, x.target.value, series)} className="h-8 w-[150px]" title={series ? "Change the usual date" : "Move this payment"} data-testid={`move-bill-date-${i}`} />}
                    {e.moved && <Button size="sm" variant="ghost" onClick={() => moveBill({ ...e }, e.originalDate)} data-testid={`reset-bill-move-${i}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Original date</Button>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {day.txs.length > 0 && (
            <ul className="divide-y text-sm">
              {day.txs.map((t) => <li key={t.id} className="py-2 flex justify-between gap-2"><span>{t.description}</span><Money value={t.amount} signed /></li>)}
            </ul>
          )}
          {!day.expected.length && !day.txs.length && <p className="text-sm text-muted-foreground">Nothing scheduled or recorded.</p>}
          {!day.isPast && day.balance !== null && <p className="text-xs text-muted-foreground mt-3">Projected balance at end of day: <span className={cn("num", day.low && "text-expense")}>{fmt(day.balance)}</span></p>}
        </Section>
      )}
    </div>
  );
}
